import { useState, useMemo, useEffect, useCallback } from 'react';

interface UseTabReviewTrackerResult {
  activeId: string;
  setActiveId: (id: string) => void;
  visitedIds: Set<string>;
  allVisited: boolean;
}

/**
 * Tracks visited tabs and reports when all have been visited.
 * Used in FamilyInfoStep (address/guardians/emergency/pickup/church tabs)
 * and StudentMedicalStep (per-student tabs).
 *
 * @param tabIds - Array of tab identifiers to track
 * @param onAllVisited - Callback fired with completion status when it changes
 */
export function useTabReviewTracker(
  tabIds: string[],
  onAllVisited?: (complete: boolean) => void
): UseTabReviewTrackerResult {
  const [activeId, setActiveIdRaw] = useState(tabIds[0] || '');
  const [visitedIds, setVisitedIds] = useState<Set<string>>(
    () => tabIds[0] ? new Set([tabIds[0]]) : new Set<string>()
  );

  const setActiveId = useCallback((id: string) => {
    setActiveIdRaw(id);
    setVisitedIds(prev => {
      if (prev.has(id)) return prev;
      return new Set([...prev, id]);
    });
  }, []);

  const allVisited = useMemo(() => {
    if (tabIds.length === 0) return true;
    if (tabIds.length === 1) return true;
    return tabIds.every(id => visitedIds.has(id));
  }, [tabIds, visitedIds]);

  useEffect(() => {
    onAllVisited?.(allVisited);
  }, [allVisited, onAllVisited]);

  return { activeId, setActiveId, visitedIds, allVisited };
}
